import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { AppBar, Box, Stack, Toolbar, CircularProgress, Skeleton } from '@mui/material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import { IconButtonAnimate } from '../../../components/animate';
//
import { useLocation } from 'react-router-dom';
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';
import { useQuery } from '@tanstack/react-query';
import { PATH_MANAGEMENT } from '../../../routes/paths';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { can } from 'src/components/permission/Permission';
import { PermissionPath } from 'src/components/permission/PermissionPath';
import React, { useEffect, useState } from 'react';
import { requests } from 'src/utils/urls';
import axios from 'src/utils/axios';
import MenuIcon from '@mui/icons-material/Menu';

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

const customStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: '80px',
    width: '100px',
    maxWidth: '100px',
    fontSize: '0.813rem',
    backgroundColor: 'rgba(145, 158, 171, 0.12)',
    border: 'none',
    borderRadius: 4,
    boxShadow: 'none',
    '@media (min-width: 600px)': {
      minWidth: '120px',
      width: '150px',
      maxWidth: '150px',
      fontSize: '0.875rem',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 4px',
    '@media (min-width: 600px)': {
      padding: '2px 8px',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'blue' : 'white',
    color: state.isSelected ? 'white' : 'black',
  }),
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const isDesktop = useResponsive('up', 'lg');
  const { rootShopId, courierId } = useParams();
  const location = useLocation();

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusInquiry, setStatusInquiry] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    if (rootShopId) {
      axios
        .get(requests.management.shop.liteSearch)
        .then((response) => {
          setShop(response?.data?.data?.shops);
          setLoading(false);
        })
        .catch((error) => {
          console.error('خطا در انجام درخواست:', error);
          setLoading(false);
        });
    }
  }, [rootShopId]);

  const fetchStatusInquiry = async () => {
    if (rootShopId) {
      setStatusLoading(true);
      try {
        const response = await axios.get(requests.management.shop.getStatusInquiry(rootShopId));
        setStatusInquiry(response?.data?.data?.shops);
        setStatusLoading(false);
      } catch (error) {
        console.error('خطا در انجام درخواست:', error);
        setStatusLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchStatusInquiry();
  }, [rootShopId]);

  const shopsData = shop?.map((shop) => ({
    value: shop.id,
    label: shop.name,
  }));
  const targetObject = shopsData?.find((obj) => obj.value === rootShopId);

  const [deliveryInfo, setDeliveryInfo] = useState(null);

  useEffect(() => {
    if (courierId) {
      axios
        .get(requests.management.courier.getAll)
        .then((response) => {
          setDeliveryInfo(response?.data?.data?.couriers);
          setLoading(false);
        })
        .catch((error) => {
          console.error('خطا در انجام درخواست:', error);
        });
    }
  }, [courierId]);

  const courierData = deliveryInfo?.map((courier) => ({
    value: courier.id,
    label: courier.name,
  }));

  const targetObjectCourier = courierData?.find((courier) => courier.value === courierId);

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { xs: 0.5, sm: 2, lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 0.25, p: { xs: 0.5, sm: 1 }, color: 'text.primary' }}>
            <MenuIcon />
          </IconButtonAnimate>
        )}

        {(location.pathname.startsWith('/management/courier/edit/') ||
          location.pathname.startsWith('/management/shop/edit/')) &&
        loading ? (
          <Skeleton variant="rectangular" width={190} height={40} />
        ) : (
          <>
            {rootShopId && (
              <Select
                placeholder="انتخاب کنید"
                onChange={(e) => {
                  window.location.href = PATH_MANAGEMENT.management.editShop.shopInvioce(e.value);
                }}
                options={shopsData}
                value={targetObject}
                isDisabled={!PermissionPath.shop.index ? can(PermissionPath.shop.index) : ''}
                styles={customStyles}
              />
            )}
            {courierId && (
              <Select
                placeholder="انتخاب کنید"
                onChange={(e) => {
                  window.location.href = PATH_MANAGEMENT.management.courier.editCourier.courierInvioce(e.value);
                }}
                options={courierData}
                value={targetObjectCourier}
                isDisabled={!PermissionPath.courier.index ? can(PermissionPath.courier.index) : ''}
                styles={customStyles}
              />
            )}
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1, md: 1.5 }}>
          {rootShopId &&
            (statusLoading ? (
              <CircularProgress size={20} />
            ) : (
              <NotificationsPopover
                rootShopId={rootShopId}
                statusInquiry={statusInquiry}
                onStatusChange={fetchStatusInquiry}
              />
            ))}
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}